import React, { Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import { DictateOrType } from "./components/DictateOrType";
import ClinicalFormPage from "./pages/forms/ClinicalFormPage";
import ExactFormPage from "./pages/forms/ExactFormPage";
import ProtectedRoute from "./components/ProtectedRoute";
import { SkipToMain } from "./components/Accessibility";
import { TranslationProvider } from "./context/TranslationContext";
import './i18n';

// Auth Pages (Public)
import LoginPage from "./pages/LoginPage";
import PublicLandingPage from "./pages/PublicLandingPage";
import ConciergeContactPage from "./pages/ConciergeContactPage";

// Dashboard Pages (Protected)
import DashboardHome from "./pages/DashboardHome";
import MainDashboard from "./pages/MainDashboard";
import BillingDashboard from "./pages/BillingDashboard";
import PatientsPage from "./pages/PatientsPage";
import PatientDemographics from "./pages/PatientDemographics";
import SchedulingPage from "./pages/SchedulingPage";
import DocumentationPage from "./pages/DocumentationPage";
import BillingPage from "./pages/BillingPage";
import ReportsPage from "./pages/ReportsPage";
import RevenueCyclePage from "./pages/RevenueCyclePage";
import QACenterPage from "./pages/QACenterPage";
import AlertsPage from "./pages/AlertsPage";
import QuickLinksPage from "./pages/QuickLinksPage";

// Clinical Forms Pages
import ClinicalFormsPage from "./pages/ClinicalFormsPage";
import AdmissionConsentForm from "./pages/AdmissionConsentForm";
import OasisAssessmentForm from "./pages/OasisAssessmentForm";
import DischargeAssessmentForm from "./pages/DischargeAssessmentForm";
import SOCROCAssessmentForm from "./pages/SOCROCAssessmentFormV2";
import RecertificationAssessmentForm from "./pages/RecertificationAssessmentForm";
import OasisE2AssessmentPage from "./pages/OasisE2AssessmentPage";
import OasisSignQueuePage from './pages/OasisSignQueuePage';

// Documentation Forms Pages
import NursesNotesForm from "./pages/NursesNotesForm";
import WoundCareForm from "./pages/WoundCareForm";
import FaceToFaceForm from "./pages/FaceToFaceForm";
import IVTherapyForm from "./pages/IVTherapyForm";

// Clinical Section Pages
import POC485Page from "./pages/clinical/POC485Page";
import POC485FormPage from "./pages/clinical/POC485FormPage";
import POCPlusPage from "./pages/clinical/POCPlusPage";
import OrdersDocumentsPage from "./pages/clinical/OrdersDocumentsPage";
import OrderPlusPage from "./pages/clinical/OrderPlusPage";
import MedicationProfilePage from "./pages/clinical/MedicationProfilePage";
import MedicationLogPage from "./pages/clinical/MedicationLogPage";
import PTDocumentsPage from "./pages/clinical/PTDocumentsPage";
import OTDocumentsPage from "./pages/clinical/OTDocumentsPage";
import STDocumentsPage from "./pages/clinical/STDocumentsPage";
import MSWDocumentsPage from "./pages/clinical/MSWDocumentsPage";
import AideDocumentsPage from "./pages/clinical/AideDocumentsPage";
import NonSkilledAssessmentPage from "./pages/clinical/NonSkilledAssessmentPage";
import EHRPage from "./pages/clinical/EHRPage";
import BradenScalePage from "./pages/clinical/BradenScalePage";
import SupervisoryNotesPage from "./pages/clinical/SupervisoryNotesPage";
import IncidentReportPage from "./pages/clinical/IncidentReportPage";
import COVIDScreeningPage from "./pages/clinical/COVIDScreeningPage";

// Patient Communication Log
import CommunicationLogPage from "./pages/CommunicationLogPage";

// IvoryMail
import IvoryMailPage from "./pages/IvoryMailPage";

// Analytics Dashboard
import AnalyticsDashboardPage from "./pages/AnalyticsDashboardPage";

// Skilled Nursing Forms
import ComprehensiveNursesNotes from "./pages/skilled-nursing/ComprehensiveNursesNotes";
import WoundCareFlowSheet from "./pages/skilled-nursing/WoundCareFlowSheet";
import IVTherapyAdministration from "./pages/skilled-nursing/IVTherapyAdministration";
import PCADocumentation from "./pages/skilled-nursing/PCADocumentation";

// AI Document Generator
import DocumentGeneratorPage from "./pages/DocumentGeneratorPage";
import SignPlanOfCarePage from "./pages/SignPlanOfCarePage";
import SignConsentPage from "./pages/SignConsentPage";
import SignOrderPage from "./pages/SignOrderPage";
import OrderSignaturesPage from "./pages/OrderSignaturesPage";
import TutorialsPage from "./pages/TutorialsPage";
import TimeClockPage from "./pages/TimeClockPage";
import StaffHubPage from "./pages/StaffHubPage";
import UploadHubPage from "./pages/UploadHubPage";
import AutoPopulateSummaryPage from "./pages/AutoPopulateSummaryPage";

// Document Conversion (Handwritten to Text)
import DocumentConversionPage from "./pages/tools/DocumentConversionPage";

// Patient Sub-Menu Pages
import AdmissionInsurancePage from "./pages/AdmissionInsurancePage";
import PatientAdmissionDetail from "./pages/PatientAdmissionDetail";
import PatientChartPage from "./pages/PatientChartPage";
import PatientDemographicsDetailPage from "./pages/PatientDemographicsDetailPage";

// Clinic Pages (Office Ally Practice Mate Clone)
import ClinicDesktop from "./pages/clinic/ClinicDesktop";

// Admin/Setup Pages - Generic Template
import GenericSetupPage from "./pages/setup/GenericSetupPage";
import AideServiceTitlesPage from "./pages/setup/AideServiceTitlesPage";
import ARAdjustmentReasonPage from "./pages/setup/ARAdjustmentReasonPage";
import AideTaskCodesPage from "./pages/setup/AideTaskCodesPage";
import BillingCodePage from "./pages/setup/BillingCodePage";
import StaffCaregiverPage from "./pages/setup/StaffCaregiverPage";
import CarePlanTemplatesPage from "./pages/setup/CarePlanTemplatesPage";
import CommunicationTypeStaffPage from "./pages/setup/CommunicationTypeStaffPage";
import ElectronicStaffFolderPage from "./pages/setup/ElectronicStaffFolderPage";
import CBSACodesPage from "./pages/setup/CBSACodesPage";
import DisciplinePage from "./pages/setup/DisciplinePage";
import EmergencyContactsPage from "./pages/setup/EmergencyContactsPage";
import EvacuationLevelPage from "./pages/setup/EvacuationLevelPage";
import EVVChangeReasonsPage from "./pages/setup/EVVChangeReasonsPage";
import GeneralLedgerPage from "./pages/setup/GeneralLedgerPage";
import InsuranceCaseManagerPage from "./pages/setup/InsuranceCaseManagerPage";
import MissedVisitReasonPage from "./pages/setup/MissedVisitReasonPage";
import OfficePage from "./pages/setup/OfficePage";
import PatientCommunicationTypePage from "./pages/setup/PatientCommunicationTypePage";
import PatientElectronicFolderPage from "./pages/setup/PatientElectronicFolderPage";
import PayerPage from "./pages/setup/PayerPage";
import PatientFacilitiesPage from "./pages/setup/PatientFacilitiesPage";
import PhrasesPage from "./pages/setup/PhrasesPage";
import PayRatePage from "./pages/setup/PayRatePage";
import PhysicianPage from "./pages/setup/PhysicianPage";
import ReasonNotAdmittedPage from "./pages/setup/ReasonNotAdmittedPage";
import ReferralSourcePage from "./pages/setup/ReferralSourcePage";
import RegionPage from "./pages/setup/RegionPage";
import SupplyCodePage from "./pages/setup/SupplyCodePage";
import AddOnPayRatePage from "./pages/setup/AddOnPayRatePage";
import IncidentTypePage from "./pages/setup/IncidentTypePage";
import EarningsCodesPage from "./pages/setup/EarningsCodesPage";

// Policy & Procedure
import PolicyProcedurePage from "./pages/PolicyProcedurePage";

// Clinic Sub-pages
import ClinicAppointments from "./pages/clinic/ClinicAppointments";
import ClinicPatientVisits from "./pages/clinic/ClinicPatientVisits";
import ClinicClaimsBilling from "./pages/clinic/ClinicClaimsBilling";
import ClinicAccounting from "./pages/clinic/ClinicAccounting";
import ClinicManagePatients from "./pages/clinic/ClinicManagePatients";
import ClinicPatientPortal from "./pages/clinic/ClinicPatientPortal";
import ClinicManageOffice from "./pages/clinic/ClinicManageOffice";

// Tools Pages
import NotificationManagerPage from "./pages/tools/NotificationManagerPage";
import TravelTimeCalculatorPage from "./pages/tools/TravelTimeCalculatorPage";
import ElectronicRemittancePage from "./pages/tools/ElectronicRemittancePage";
import OASISSeguePage from "./pages/tools/OASISSeguePage";
import DocumentLibraryPage from "./pages/tools/DocumentLibraryPage";
import DataImportPage from "./pages/tools/DataImportPage";
import OASISExportPage from "./pages/tools/OASISExportPage";
import SignatureAuditTrailPage from "./pages/SignatureAuditTrailPage";
import QAPIPage from "./pages/QAPIPage";
import HIPAAComplianceDashboard from "./pages/HIPAAComplianceDashboard";
import SecuritySettingsPage from "./pages/SecuritySettingsPage";
import UserManagementPage from "./pages/UserManagementPage";

// Inovalon Pages
import ClaimsManagementProPage from "./pages/inovalon/ClaimsManagementProPage";
import ClaimsMedicareProPage from "./pages/inovalon/ClaimsMedicareProPage";
import EligibilityWorkflowPage from "./pages/inovalon/EligibilityWorkflowPage";

// Admin Pages
import CustomizePage from "./pages/admin/CustomizePage";
import AdminFeaturesActivationPage from "./pages/admin/FeaturesActivationPage";
import AccountBillingPage from "./pages/admin/AccountBillingPage";
import TranslationMemoryPage from "./pages/admin/TranslationMemoryPage";
import StaffManagementPage from "./pages/admin/StaffManagementPage";
import StaffCredentialsPage from "./pages/admin/StaffCredentialsPage";
import AuditLogPage from "./pages/admin/AuditLogPage";

// Behavioral Health Pages
import BehavioralHealthDashboard from "./pages/BehavioralHealthDashboard";
import BehavioralHealthTelehealth from "./pages/BehavioralHealthTelehealth";
import BehavioralHealthClients from "./pages/BehavioralHealthClients";
import BehavioralHealthNotes from "./pages/BehavioralHealthNotes";
import BehavioralHealthMessages from "./pages/BehavioralHealthMessages";
import BehavioralHealthSchedule from "./pages/BehavioralHealthSchedule";

// Help & Training Pages
import FAQPage from "./pages/FAQPage";
import FeaturesActivationPage from "./pages/FeaturesActivationPage";
import LiveTrainingPage from "./pages/LiveTrainingPage";
import TrainingVideosPage from "./pages/TrainingVideosPage";

// Admission & Consent Forms
import AdmissionPacketIndex from "./pages/admission-consent/AdmissionPacketIndex";
import EmergencyInfoForm from "./pages/admission-consent/EmergencyInfoForm";
import BillingAgreementForm from "./pages/admission-consent/BillingAgreementForm";
import ConsentVerificationForm from "./pages/admission-consent/ConsentVerificationForm";
import PersonalCareHomeAssessment from "./pages/admission-consent/PersonalCareHomeAssessment";
import GenericAdmissionForm from "./pages/admission-consent/GenericAdmissionForm";

// Service Pages
import ServicePage from "./pages/services/ServicePage";

// Fillable Templates
import CMS485TemplateForm from "./pages/admission-consent/CMS485TemplateForm";
import PatientFaceSheetTemplate from "./pages/admission-consent/PatientFaceSheetTemplate";

function App() {
  return (
    <HelmetProvider>
      <TranslationProvider>
      <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#ea580c]"></div></div>}>
        <div className="App">
          <SkipToMain />
          <BrowserRouter>
            <Routes>
              {/* Public Routes - Login Only */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/sign/485/:token" element={<SignPlanOfCarePage />} />
              <Route path="/sign/consent/:token" element={<SignConsentPage />} />
              <Route path="/sign/order/:token" element={<SignOrderPage />} />
          
          {/* Public Landing Page */}
          <Route path="/" element={<PublicLandingPage />} />
          <Route path="/home" element={<PublicLandingPage />} />
          
          {/* Other Public Pages redirect to landing */}
          <Route path="/solutions/*" element={<PublicLandingPage />} />
          <Route path="/about" element={<PublicLandingPage />} />
          <Route path="/contact" element={<PublicLandingPage />} />
          <Route path="/career" element={<PublicLandingPage />} />
          <Route path="/faq" element={<PublicLandingPage />} />
          <Route path="/blog" element={<PublicLandingPage />} />
          <Route path="/testimonials" element={<PublicLandingPage />} />
          <Route path="/register" element={<Navigate to="/login" replace />} />
          
          {/* Concierge / Visiting Doctor Page */}
          <Route path="/concierge" element={<ConciergeContactPage />} />

          {/* Protected Dashboard Routes */}
          <Route path="/dashboard" element={<ProtectedRoute><DashboardHome /></ProtectedRoute>} />
          <Route path="/dashboard/quick-links" element={<ProtectedRoute><QuickLinksPage /></ProtectedRoute>} />
          <Route path="/dashboard/main" element={<ProtectedRoute><MainDashboard /></ProtectedRoute>} />
          <Route path="/dashboard/billing-dashboard" element={<ProtectedRoute><BillingDashboard /></ProtectedRoute>} />
          
          {/* Patient Routes */}
          <Route path="/dashboard/patients" element={<ProtectedRoute><PatientsPage /></ProtectedRoute>} />
          <Route path="/dashboard/patients/demographics" element={<ProtectedRoute><PatientDemographics /></ProtectedRoute>} />
          <Route path="/dashboard/patients/demographics/:patientId" element={<ProtectedRoute><PatientDemographicsDetailPage /></ProtectedRoute>} />
          <Route path="/dashboard/patients/admission" element={<ProtectedRoute><AdmissionInsurancePage /></ProtectedRoute>} />
          <Route path="/dashboard/patients/admission/:patientId" element={<ProtectedRoute><PatientAdmissionDetail /></ProtectedRoute>} />
          <Route path="/dashboard/patients/chart" element={<ProtectedRoute><PatientChartPage /></ProtectedRoute>} />
          <Route path="/dashboard/patient/chart" element={<ProtectedRoute><PatientChartPage /></ProtectedRoute>} />
          <Route path="/dashboard/patients/communication" element={<ProtectedRoute><CommunicationLogPage /></ProtectedRoute>} />
          
          {/* IvoryMail */}
          <Route path="/dashboard/ivorymail" element={<ProtectedRoute><IvoryMailPage /></ProtectedRoute>} />
          
          {/* Analytics Dashboard */}
          <Route path="/dashboard/analytics" element={<ProtectedRoute><AnalyticsDashboardPage /></ProtectedRoute>} />
          
          {/* Clinical Forms Routes */}
          <Route path="/dashboard/clinical/oasis" element={<ProtectedRoute><ClinicalFormsPage /></ProtectedRoute>} />
          <Route path="/dashboard/clinical/orders" element={<ProtectedRoute><OrdersDocumentsPage /></ProtectedRoute>} />
          <Route path="/dashboard/clinical/poc" element={<ProtectedRoute><POC485Page /></ProtectedRoute>} />
          <Route path="/dashboard/forms/admission-consent" element={<ProtectedRoute><AdmissionConsentForm /></ProtectedRoute>} />
          <Route path="/dashboard/forms/oasis" element={<ProtectedRoute><OasisE2AssessmentPage defaultTp="SOC" /></ProtectedRoute>} />
          <Route path="/dashboard/forms/oasis-e2/sign-list" element={<ProtectedRoute><OasisSignQueuePage /></ProtectedRoute>} />
          <Route path="/dashboard/forms/oasis-e2" element={<ProtectedRoute><OasisE2AssessmentPage defaultTp="SOC" /></ProtectedRoute>} />
          <Route path="/dashboard/forms/oasis-legacy" element={<ProtectedRoute><OasisAssessmentForm /></ProtectedRoute>} />
          <Route path="/dashboard/forms/non-oasis" element={<ProtectedRoute><OasisAssessmentForm /></ProtectedRoute>} />
          <Route path="/dashboard/forms/discharge" element={<ProtectedRoute><OasisE2AssessmentPage key="DC" defaultTp="DC" /></ProtectedRoute>} />
          <Route path="/dashboard/forms/discharge-legacy" element={<ProtectedRoute><DischargeAssessmentForm /></ProtectedRoute>} />
          <Route path="/dashboard/forms/soc-roc" element={<ProtectedRoute><OasisE2AssessmentPage key="SOC" defaultTp="SOC" /></ProtectedRoute>} />
          <Route path="/dashboard/forms/soc-roc-legacy" element={<ProtectedRoute><SOCROCAssessmentForm /></ProtectedRoute>} />
          <Route path="/dashboard/forms/discharge-v2" element={<ProtectedRoute><OasisE2AssessmentPage key="DC2" defaultTp="DC" /></ProtectedRoute>} />
          <Route path="/dashboard/forms/recertification" element={<ProtectedRoute><OasisE2AssessmentPage key="FU" defaultTp="FU" /></ProtectedRoute>} />
          <Route path="/dashboard/forms/recertification-legacy" element={<ProtectedRoute><RecertificationAssessmentForm /></ProtectedRoute>} />
          
          {/* Documentation Forms Routes */}
          <Route path="/dashboard/forms/nurses-notes" element={<ProtectedRoute><NursesNotesForm /></ProtectedRoute>} />
          <Route path="/dashboard/forms/wound-care" element={<ProtectedRoute><WoundCareForm /></ProtectedRoute>} />
          <Route path="/dashboard/forms/face-to-face" element={<ProtectedRoute><FaceToFaceForm /></ProtectedRoute>} />
          <Route path="/dashboard/forms/iv-therapy" element={<ProtectedRoute><IVTherapyForm /></ProtectedRoute>} />
          
          {/* Clinical Section Routes - All 18 items */}
          <Route path="/dashboard/clinical/poc-485" element={<ProtectedRoute><POC485Page /></ProtectedRoute>} />
          <Route path="/dashboard/clinical/poc-485/form" element={<ProtectedRoute><POC485FormPage /></ProtectedRoute>} />
          <Route path="/dashboard/clinical/poc-485/edit" element={<ProtectedRoute><POC485FormPage /></ProtectedRoute>} />
          <Route path="/dashboard/clinical/poc-plus" element={<ProtectedRoute><POCPlusPage /></ProtectedRoute>} />
          <Route path="/dashboard/clinical/order-plus" element={<ProtectedRoute><OrderPlusPage /></ProtectedRoute>} />
          <Route path="/dashboard/clinical/medication-profile" element={<ProtectedRoute><MedicationProfilePage /></ProtectedRoute>} />
          <Route path="/dashboard/clinical/medication-log" element={<ProtectedRoute><MedicationLogPage /></ProtectedRoute>} />
          <Route path="/dashboard/clinical/pt-documents" element={<ProtectedRoute><PTDocumentsPage /></ProtectedRoute>} />
          <Route path="/dashboard/clinical/ot-documents" element={<ProtectedRoute><OTDocumentsPage /></ProtectedRoute>} />
          <Route path="/dashboard/clinical/st-documents" element={<ProtectedRoute><STDocumentsPage /></ProtectedRoute>} />
          <Route path="/dashboard/clinical/msw-documents" element={<ProtectedRoute><MSWDocumentsPage /></ProtectedRoute>} />
          <Route path="/dashboard/clinical/aide-documents" element={<ProtectedRoute><AideDocumentsPage /></ProtectedRoute>} />
          <Route path="/dashboard/clinical/non-skilled" element={<ProtectedRoute><NonSkilledAssessmentPage /></ProtectedRoute>} />
          <Route path="/dashboard/clinical/ehr" element={<ProtectedRoute><EHRPage /></ProtectedRoute>} />
          <Route path="/dashboard/clinical/braden-scale" element={<ProtectedRoute><BradenScalePage /></ProtectedRoute>} />
          <Route path="/dashboard/clinical/supervisory-notes" element={<ProtectedRoute><SupervisoryNotesPage /></ProtectedRoute>} />
          <Route path="/dashboard/clinical/incident-report" element={<ProtectedRoute><IncidentReportPage /></ProtectedRoute>} />
          
          {/* COVID-19 Screening */}
          <Route path="/dashboard/covid-screening" element={<ProtectedRoute><COVIDScreeningPage /></ProtectedRoute>} />
          
          {/* AI Document Generator */}
          <Route path="/dashboard/document-generator" element={<ProtectedRoute><DocumentGeneratorPage /></ProtectedRoute>} />
          <Route path="/dashboard/auto-populate-summary/:patientId" element={<ProtectedRoute><AutoPopulateSummaryPage /></ProtectedRoute>} />
          
          {/* Skilled Nursing Forms Routes */}
          <Route path="/dashboard/skilled-nursing/nurses-notes" element={<ProtectedRoute><ComprehensiveNursesNotes /></ProtectedRoute>} />
          <Route path="/dashboard/skilled-nursing/wound-care" element={<ProtectedRoute><WoundCareFlowSheet /></ProtectedRoute>} />
          <Route path="/dashboard/skilled-nursing/iv-therapy" element={<ProtectedRoute><IVTherapyAdministration /></ProtectedRoute>} />
          <Route path="/dashboard/skilled-nursing/pca" element={<ProtectedRoute><PCADocumentation /></ProtectedRoute>} />
          
          {/* Documentation */}
          <Route path="/dashboard/documentation" element={<ProtectedRoute><DocumentationPage /></ProtectedRoute>} />
          
          {/* Other Routes */}
          <Route path="/dashboard/scheduling" element={<ProtectedRoute><SchedulingPage /></ProtectedRoute>} />
          <Route path="/dashboard/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
          <Route path="/dashboard/billing/rap" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
          <Route path="/dashboard/billing/noa" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
          <Route path="/dashboard/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
          <Route path="/dashboard/alerts" element={<ProtectedRoute><AlertsPage /></ProtectedRoute>} />
          <Route path="/dashboard/mail" element={<ProtectedRoute><AlertsPage /></ProtectedRoute>} />
          <Route path="/dashboard/ar" element={<ProtectedRoute><RevenueCyclePage /></ProtectedRoute>} />
          <Route path="/dashboard/ar/aging" element={<ProtectedRoute><RevenueCyclePage /></ProtectedRoute>} />
          <Route path="/dashboard/ar/collections" element={<ProtectedRoute><RevenueCyclePage /></ProtectedRoute>} />
          <Route path="/dashboard/billing/claims" element={<ProtectedRoute><RevenueCyclePage /></ProtectedRoute>} />
          <Route path="/dashboard/billing/submission" element={<ProtectedRoute><RevenueCyclePage /></ProtectedRoute>} />
          <Route path="/dashboard/billing/payment-posting" element={<ProtectedRoute><RevenueCyclePage /></ProtectedRoute>} />
          <Route path="/dashboard/qa" element={<ProtectedRoute><QACenterPage /></ProtectedRoute>} />
          <Route path="/dashboard/order-signatures" element={<ProtectedRoute><OrderSignaturesPage /></ProtectedRoute>} />
          <Route path="/dashboard/tutorials" element={<ProtectedRoute><TutorialsPage /></ProtectedRoute>} />
          <Route path="/dashboard/time-clock" element={<ProtectedRoute><TimeClockPage /></ProtectedRoute>} />
          <Route path="/dashboard/staff-hub" element={<ProtectedRoute><StaffHubPage /></ProtectedRoute>} />
          <Route path="/dashboard/upload-hub" element={<ProtectedRoute><UploadHubPage /></ProtectedRoute>} />
          <Route path="/dashboard/staff-hub/:doc" element={<ProtectedRoute><StaffHubPage /></ProtectedRoute>} />
          <Route path="/dashboard/setup" element={<ProtectedRoute><AlertsPage /></ProtectedRoute>} />
          <Route path="/dashboard/tools" element={<ProtectedRoute><AlertsPage /></ProtectedRoute>} />
          <Route path="/dashboard/help" element={<ProtectedRoute><AlertsPage /></ProtectedRoute>} />
          
          {/* Admin/Setup Routes - All Alora Plus Setup Pages */}
          <Route path="/dashboard/setup/aide-service-titles" element={<ProtectedRoute><AideServiceTitlesPage /></ProtectedRoute>} />
          <Route path="/dashboard/setup/ar-adjustment-reason" element={<ProtectedRoute><ARAdjustmentReasonPage /></ProtectedRoute>} />
          <Route path="/dashboard/setup/aide-task-codes" element={<ProtectedRoute><AideTaskCodesPage /></ProtectedRoute>} />
          <Route path="/dashboard/setup/billing-code" element={<ProtectedRoute><BillingCodePage /></ProtectedRoute>} />
          <Route path="/dashboard/setup/staff" element={<ProtectedRoute><StaffCaregiverPage /></ProtectedRoute>} />
          <Route path="/dashboard/setup/care-plan-templates" element={<ProtectedRoute><CarePlanTemplatesPage /></ProtectedRoute>} />
          <Route path="/dashboard/setup/communication-type-staff" element={<ProtectedRoute><CommunicationTypeStaffPage /></ProtectedRoute>} />
          <Route path="/dashboard/setup/electronic-staff-folder" element={<ProtectedRoute><ElectronicStaffFolderPage /></ProtectedRoute>} />
          <Route path="/dashboard/setup/cbsa-codes" element={<ProtectedRoute><CBSACodesPage /></ProtectedRoute>} />
          <Route path="/dashboard/setup/discipline" element={<ProtectedRoute><DisciplinePage /></ProtectedRoute>} />
          <Route path="/dashboard/setup/emergency-contacts" element={<ProtectedRoute><EmergencyContactsPage /></ProtectedRoute>} />
          <Route path="/dashboard/setup/evacuation-level" element={<ProtectedRoute><EvacuationLevelPage /></ProtectedRoute>} />
          <Route path="/dashboard/setup/evv-change-reasons" element={<ProtectedRoute><EVVChangeReasonsPage /></ProtectedRoute>} />
          <Route path="/dashboard/setup/general-ledger" element={<ProtectedRoute><GeneralLedgerPage /></ProtectedRoute>} />
          <Route path="/dashboard/setup/insurance-case-manager" element={<ProtectedRoute><InsuranceCaseManagerPage /></ProtectedRoute>} />
          <Route path="/dashboard/setup/missed-visit-reason" element={<ProtectedRoute><MissedVisitReasonPage /></ProtectedRoute>} />
          <Route path="/dashboard/setup/office" element={<ProtectedRoute><OfficePage /></ProtectedRoute>} />
          <Route path="/dashboard/setup/patient-communication-type" element={<ProtectedRoute><PatientCommunicationTypePage /></ProtectedRoute>} />
          <Route path="/dashboard/setup/patient-electronic-folder" element={<ProtectedRoute><PatientElectronicFolderPage /></ProtectedRoute>} />
          <Route path="/dashboard/setup/payer" element={<ProtectedRoute><PayerPage /></ProtectedRoute>} />
          <Route path="/dashboard/setup/patient-facilities" element={<ProtectedRoute><PatientFacilitiesPage /></ProtectedRoute>} />
          <Route path="/dashboard/setup/phrases" element={<ProtectedRoute><PhrasesPage /></ProtectedRoute>} />
          <Route path="/dashboard/setup/pay-rate" element={<ProtectedRoute><PayRatePage /></ProtectedRoute>} />
          <Route path="/dashboard/setup/physician" element={<ProtectedRoute><PhysicianPage /></ProtectedRoute>} />
          <Route path="/dashboard/setup/reason-not-admitted" element={<ProtectedRoute><ReasonNotAdmittedPage /></ProtectedRoute>} />
          <Route path="/dashboard/setup/referral-source" element={<ProtectedRoute><ReferralSourcePage /></ProtectedRoute>} />
          <Route path="/dashboard/setup/region" element={<ProtectedRoute><RegionPage /></ProtectedRoute>} />
          <Route path="/dashboard/setup/supply-code" element={<ProtectedRoute><SupplyCodePage /></ProtectedRoute>} />
          <Route path="/dashboard/setup/addon-pay-rate" element={<ProtectedRoute><AddOnPayRatePage /></ProtectedRoute>} />
          <Route path="/dashboard/setup/incident-type" element={<ProtectedRoute><IncidentTypePage /></ProtectedRoute>} />
          <Route path="/dashboard/setup/earnings-codes" element={<ProtectedRoute><EarningsCodesPage /></ProtectedRoute>} />
          
          {/* Tools Routes */}
          <Route path="/dashboard/tools/notification-manager" element={<ProtectedRoute><NotificationManagerPage /></ProtectedRoute>} />
          <Route path="/dashboard/tools/travel-time-calculator" element={<ProtectedRoute><TravelTimeCalculatorPage /></ProtectedRoute>} />
          <Route path="/dashboard/tools/electronic-remittance" element={<ProtectedRoute><ElectronicRemittancePage /></ProtectedRoute>} />
          <Route path="/dashboard/tools/oasis-segue" element={<ProtectedRoute><OASISSeguePage /></ProtectedRoute>} />
          <Route path="/dashboard/tools/document-library" element={<ProtectedRoute><DocumentLibraryPage /></ProtectedRoute>} />
          <Route path="/dashboard/tools/data-import" element={<ProtectedRoute><DataImportPage /></ProtectedRoute>} />
          <Route path="/dashboard/tools/oasis-export" element={<ProtectedRoute><OASISExportPage /></ProtectedRoute>} />
          <Route path="/dashboard/tools/signature-audit" element={<ProtectedRoute><SignatureAuditTrailPage /></ProtectedRoute>} />
          <Route path="/dashboard/tools/document-conversion" element={<ProtectedRoute><DocumentConversionPage /></ProtectedRoute>} />
          
          {/* QAPI Route */}
          <Route path="/dashboard/qapi" element={<ProtectedRoute><QAPIPage /></ProtectedRoute>} />
          
          {/* Policy & Procedure */}
          <Route path="/dashboard/policy-procedure" element={<ProtectedRoute><PolicyProcedurePage /></ProtectedRoute>} />
          
          {/* HIPAA Compliance Routes */}
          <Route path="/dashboard/hipaa" element={<ProtectedRoute><HIPAAComplianceDashboard /></ProtectedRoute>} />
          <Route path="/dashboard/security-settings" element={<ProtectedRoute><SecuritySettingsPage /></ProtectedRoute>} />
          
          {/* User Management (Admin Only) */}
          <Route path="/dashboard/admin/users" element={<ProtectedRoute><UserManagementPage /></ProtectedRoute>} />
          <Route path="/dashboard/admin/customize" element={<ProtectedRoute><CustomizePage /></ProtectedRoute>} />
          <Route path="/dashboard/admin/features" element={<ProtectedRoute><AdminFeaturesActivationPage /></ProtectedRoute>} />
          <Route path="/dashboard/admin/billing" element={<ProtectedRoute><AccountBillingPage /></ProtectedRoute>} />
          <Route path="/dashboard/billing/success" element={<ProtectedRoute><AccountBillingPage /></ProtectedRoute>} />
          <Route path="/dashboard/admin/translation-memory" element={<ProtectedRoute><TranslationMemoryPage /></ProtectedRoute>} />
          <Route path="/dashboard/admin/staff" element={<ProtectedRoute><StaffManagementPage /></ProtectedRoute>} />
          <Route path="/dashboard/admin/credentials" element={<ProtectedRoute><StaffCredentialsPage /></ProtectedRoute>} />
          <Route path="/dashboard/admin/audit" element={<ProtectedRoute><AuditLogPage /></ProtectedRoute>} />
          
          {/* Inovalon Routes */}
          <Route path="/dashboard/inovalon/claims-pro" element={<ProtectedRoute><ClaimsManagementProPage /></ProtectedRoute>} />
          <Route path="/dashboard/inovalon/claims-medicare-pro" element={<ProtectedRoute><ClaimsMedicareProPage /></ProtectedRoute>} />
          <Route path="/dashboard/inovalon/claims-management" element={<ProtectedRoute><ClaimsManagementProPage /></ProtectedRoute>} />
          <Route path="/dashboard/inovalon/eligibility-workflow" element={<ProtectedRoute><EligibilityWorkflowPage /></ProtectedRoute>} />
          
          {/* Clinic Routes (Office Ally Practice Mate Clone) */}
          <Route path="/dashboard/clinic" element={<ProtectedRoute><ClinicDesktop /></ProtectedRoute>} />
          <Route path="/dashboard/clinic/appointments" element={<ProtectedRoute><ClinicAppointments /></ProtectedRoute>} />
          <Route path="/dashboard/clinic/patient-visits" element={<ProtectedRoute><ClinicPatientVisits /></ProtectedRoute>} />
          <Route path="/dashboard/clinic/claims" element={<ProtectedRoute><ClinicClaimsBilling /></ProtectedRoute>} />
          <Route path="/dashboard/clinic/accounting" element={<ProtectedRoute><ClinicAccounting /></ProtectedRoute>} />
          <Route path="/dashboard/clinic/patients" element={<ProtectedRoute><ClinicManagePatients /></ProtectedRoute>} />
          <Route path="/dashboard/clinic/portal" element={<ProtectedRoute><ClinicPatientPortal /></ProtectedRoute>} />
          <Route path="/dashboard/clinic/office" element={<ProtectedRoute><ClinicManageOffice /></ProtectedRoute>} />
          
          {/* Behavioral Health Routes */}
          <Route path="/dashboard/behavioral-health" element={<ProtectedRoute><BehavioralHealthDashboard /></ProtectedRoute>} />
          <Route path="/dashboard/behavioral-health/telehealth" element={<ProtectedRoute><BehavioralHealthTelehealth /></ProtectedRoute>} />
          <Route path="/dashboard/behavioral-health/clients" element={<ProtectedRoute><BehavioralHealthClients /></ProtectedRoute>} />
          <Route path="/dashboard/behavioral-health/clients/new" element={<ProtectedRoute><BehavioralHealthClients /></ProtectedRoute>} />
          <Route path="/dashboard/behavioral-health/clients/:id" element={<ProtectedRoute><BehavioralHealthClients /></ProtectedRoute>} />
          <Route path="/dashboard/behavioral-health/notes" element={<ProtectedRoute><BehavioralHealthNotes /></ProtectedRoute>} />
          <Route path="/dashboard/behavioral-health/messages" element={<ProtectedRoute><BehavioralHealthMessages /></ProtectedRoute>} />
          <Route path="/dashboard/behavioral-health/schedule" element={<ProtectedRoute><BehavioralHealthSchedule /></ProtectedRoute>} />
          
          {/* Help & Training Routes */}
          <Route path="/dashboard/faq" element={<ProtectedRoute><FAQPage /></ProtectedRoute>} />
          <Route path="/dashboard/features-activation" element={<ProtectedRoute><FeaturesActivationPage /></ProtectedRoute>} />
          <Route path="/dashboard/live-training" element={<ProtectedRoute><LiveTrainingPage /></ProtectedRoute>} />
          <Route path="/dashboard/training-videos" element={<ProtectedRoute><TrainingVideosPage /></ProtectedRoute>} />
          
          {/* Admission & Consent Routes */}
          <Route path="/dashboard/admission/index" element={<ProtectedRoute><AdmissionPacketIndex /></ProtectedRoute>} />
          <Route path="/dashboard/admission/emergency-info" element={<ProtectedRoute><EmergencyInfoForm /></ProtectedRoute>} />
          <Route path="/dashboard/admission/billing-agreement" element={<ProtectedRoute><BillingAgreementForm /></ProtectedRoute>} />
          <Route path="/dashboard/admission/consent-verification" element={<ProtectedRoute><ConsentVerificationForm /></ProtectedRoute>} />
          <Route path="/dashboard/admission/home-assessment" element={<ProtectedRoute><PersonalCareHomeAssessment /></ProtectedRoute>} />
          
          {/* Generic Admission Forms - handles all other admission forms */}
          <Route path="/dashboard/admission/toc" element={<ProtectedRoute><GenericAdmissionForm /></ProtectedRoute>} />
          <Route path="/dashboard/admission/welcome" element={<ProtectedRoute><GenericAdmissionForm /></ProtectedRoute>} />
          <Route path="/dashboard/admission/criteria" element={<ProtectedRoute><GenericAdmissionForm /></ProtectedRoute>} />
          <Route path="/dashboard/admission/rights" element={<ProtectedRoute><GenericAdmissionForm /></ProtectedRoute>} />
          <Route path="/dashboard/admission/agency-responsibilities" element={<ProtectedRoute><GenericAdmissionForm /></ProtectedRoute>} />
          <Route path="/dashboard/admission/grievances" element={<ProtectedRoute><GenericAdmissionForm /></ProtectedRoute>} />
          <Route path="/dashboard/admission/elderly-rights" element={<ProtectedRoute><GenericAdmissionForm /></ProtectedRoute>} />
          <Route path="/dashboard/admission/hipaa" element={<ProtectedRoute><GenericAdmissionForm /></ProtectedRoute>} />
          <Route path="/dashboard/admission/fraud-reporting" element={<ProtectedRoute><GenericAdmissionForm /></ProtectedRoute>} />
          <Route path="/dashboard/admission/abuse-policy" element={<ProtectedRoute><GenericAdmissionForm /></ProtectedRoute>} />
          <Route path="/dashboard/admission/advance-directives" element={<ProtectedRoute><GenericAdmissionForm /></ProtectedRoute>} />
          <Route path="/dashboard/admission/infection-control" element={<ProtectedRoute><GenericAdmissionForm /></ProtectedRoute>} />
          <Route path="/dashboard/admission/covid-screening" element={<ProtectedRoute><GenericAdmissionForm /></ProtectedRoute>} />
          <Route path="/dashboard/forms/clinical/:formType" element={<ProtectedRoute><ClinicalFormPage /></ProtectedRoute>} />
          <Route path="/dashboard/forms/exact/:formId" element={<ProtectedRoute><ExactFormPage /></ProtectedRoute>} />
          <Route path="/dashboard/admission/emergency-preparedness" element={<ProtectedRoute><GenericAdmissionForm /></ProtectedRoute>} />
          <Route path="/dashboard/admission/disaster-plan" element={<ProtectedRoute><GenericAdmissionForm /></ProtectedRoute>} />
          <Route path="/dashboard/admission/emergency-numbers" element={<ProtectedRoute><GenericAdmissionForm /></ProtectedRoute>} />
          <Route path="/dashboard/admission/home-safety" element={<ProtectedRoute><GenericAdmissionForm /></ProtectedRoute>} />
          <Route path="/dashboard/admission/tornado-plan" element={<ProtectedRoute><GenericAdmissionForm /></ProtectedRoute>} />
          <Route path="/dashboard/admission/evacuation" element={<ProtectedRoute><GenericAdmissionForm /></ProtectedRoute>} />
          <Route path="/dashboard/admission/risk-assessment" element={<ProtectedRoute><GenericAdmissionForm /></ProtectedRoute>} />
          <Route path="/dashboard/admission/service-plan" element={<ProtectedRoute><GenericAdmissionForm /></ProtectedRoute>} />
          <Route path="/dashboard/admission/vital-signs" element={<ProtectedRoute><GenericAdmissionForm /></ProtectedRoute>} />
          
          {/* Service Pages Routes */}
          <Route path="/dashboard/services/:serviceType" element={<ProtectedRoute><ServicePage /></ProtectedRoute>} />
          
          {/* Fillable Template Routes */}
          <Route path="/dashboard/templates/cms-485" element={<ProtectedRoute><CMS485TemplateForm /></ProtectedRoute>} />
          <Route path="/dashboard/templates/face-sheet" element={<ProtectedRoute><PatientFaceSheetTemplate /></ProtectedRoute>} />
        </Routes>
        <Toaster />
        <DictateOrType />
      </BrowserRouter>
        </div>
      </Suspense>
      </TranslationProvider>
    </HelmetProvider>
  );
}

export default App;
